import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Plus, Edit2, Trash2, FileText, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { groupResolutionsByMeeting } from '../sortUtils';

const VOTE = [
  { key: 'yes', label: 'Yes', color: '#43A047' },
  { key: 'no', label: 'No', color: '#D9534F' },
  { key: 'abstain', label: 'Abstain', color: '#FFB74D' },
  { key: 'absent', label: 'Absent', color: '#8E85A0' },
];

const empty = () => ({ meeting_id: '', motion_text: '', motioner_id: '', seconder_id: '', votes: {}, notes: '' });

export default function ResolutionsTab({ api, isAdmin, members }) {
  const [list, setList] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [filterMeeting, setFilterMeeting] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const q = filterMeeting ? `?meeting_id=${filterMeeting}` : '';
      const [r, m] = await Promise.all([api.get(`/board/resolutions${q}`), api.get('/board/meetings')]);
      setList(r.data); setMeetings(m.data);
    } catch (e) { /* noop */ }
  }, [api, filterMeeting]);
  useEffect(() => { load(); }, [load]);

  const memberName = (id) => members.find(m => m.id === id)?.name || '—';
  const meetingTitle = (id) => meetings.find(m => m.id === id)?.title || '—';

  const openCreate = () => {
    const init = empty();
    members.forEach(m => { init.votes[m.id] = 'absent'; });
    if (filterMeeting) init.meeting_id = filterMeeting;
    setEditing(null); setForm(init); setOpen(true);
  };
  const openEdit = (r) => {
    const votes = { ...(r.votes || {}) };
    members.forEach(m => { if (!votes[m.id]) votes[m.id] = 'absent'; });
    setEditing(r); setForm({ ...empty(), ...r, votes }); setOpen(true);
  };

  const setVote = (mid, v) => setForm(f => ({ ...f, votes: { ...f.votes, [mid]: v } }));

  const save = async () => {
    if (!form.meeting_id) { toast.error('Pick a meeting'); return; }
    if (!form.motion_text.trim()) { toast.error('Motion text required'); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/board/resolutions/${editing.id}`, form);
      else await api.post('/board/resolutions', form);
      toast.success('Saved'); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const del = async (r) => {
    if (!window.confirm('Delete this resolution?')) return;
    try { await api.delete(`/board/resolutions/${r.id}`); toast.success('Deleted'); load(); } catch (e) { toast.error('Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="resolutions-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-lg font-bold text-[#2D1B4E]">Resolutions & Votes</h2>
        <div className="flex items-center gap-2">
          <select value={filterMeeting} onChange={e => setFilterMeeting(e.target.value)}
            className="border border-[#DED8EB] rounded px-2 py-1.5 text-xs bg-white" data-testid="filter-meeting">
            <option value="">All meetings</option>
            {meetings.map(m => <option key={m.id} value={m.id}>{m.title}</option>)}
          </select>
          {isAdmin && <Button onClick={openCreate} className="bg-[#6B3FA0]" data-testid="add-resolution-btn"><Plus className="h-4 w-4 mr-1" /> Add Resolution</Button>}
        </div>
      </div>

      {list.length === 0 ? (
        <p className="text-center py-12 text-[#8E85A0] text-sm">No resolutions recorded.</p>
      ) : (
        <div className="space-y-5">
          {groupResolutionsByMeeting(list, meetings).map(({ meetingId, meeting, items }) => (
            <div key={meetingId} className="space-y-2" data-testid={`resolution-group-${meetingId}`}>
              <div className="flex items-center gap-2 pb-1 border-b border-[#DED8EB]">
                <Calendar className="h-3.5 w-3.5 text-[#6B3FA0]" />
                <h3 className="text-xs font-bold uppercase tracking-wider text-[#2D1B4E]">
                  {meeting?.title || (meetingId === 'unlinked' ? 'Unlinked Resolutions' : 'Unknown Meeting')}
                </h3>
                {meeting?.meeting_date && <span className="text-[10px] text-[#8E85A0]">· {meeting.meeting_date}</span>}
                <Badge className="text-[10px] bg-[#F0EDF6] text-[#6B3FA0]">{items.length}</Badge>
              </div>
              {items.map(r => {
            const t = r.tally || { counts: {}, result: '' };
            const passed = t.result === 'passed';
            const failed = t.result === 'failed';
            return (
              <Card key={r.id} className={`bg-white ${passed ? 'border-[#43A047]' : failed ? 'border-[#D9534F]' : 'border-[#DED8EB]'}`} data-testid={`resolution-${r.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <p className="font-bold text-[#2D1B4E]"><FileText className="h-4 w-4 inline mr-1 text-[#6B3FA0]" />{r.motion_text}</p>
                      <div className="text-xs text-[#5A4D6E] mt-1 flex flex-wrap gap-x-3">
                        {r.motioner_id && <span>Motion: <strong>{memberName(r.motioner_id)}</strong></span>}
                        {r.seconder_id && <span>Second: <strong>{memberName(r.seconder_id)}</strong></span>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={`text-xs font-bold ${passed ? 'bg-[#43A04722] text-[#1B5E20] border-[#43A047]' : failed ? 'bg-[#D9534F22] text-[#D9534F] border-[#D9534F]' : 'bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]'}`}>
                        {t.result.toUpperCase() || '—'}
                      </Badge>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(r)} className="h-7 w-7"><Edit2 className="h-3 w-3" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => del(r)} className="h-7 w-7 text-[#D9534F]"><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2 text-xs font-mono" data-testid={`tally-${r.id}`}>
                    {VOTE.map(v => (
                      <span key={v.key} className="px-2 py-0.5 rounded" style={{ background: `${v.color}22`, color: v.color }}>
                        {v.label}: <strong>{t.counts?.[v.key] || 0}</strong>
                      </span>
                    ))}
                  </div>
                  {r.notes && <p className="text-xs italic text-[#8E85A0] mt-2">{r.notes}</p>}
                </CardContent>
              </Card>
            );
          })}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'Record'} Resolution</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Meeting *</Label>
              <select value={form.meeting_id} onChange={e => setForm(f => ({ ...f, meeting_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white" data-testid="res-meeting">
                <option value="">— pick a meeting —</option>
                {meetings.map(m => <option key={m.id} value={m.id}>{m.title} ({m.meeting_date})</option>)}
              </select>
            </div>
            <div><Label className="text-xs">Motion Text *</Label>
              <textarea value={form.motion_text} onChange={e => setForm(f => ({ ...f, motion_text: e.target.value }))} rows={2} className="w-full border border-[#DED8EB] rounded px-2 py-1 text-sm" data-testid="res-motion" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Motioner</Label>
                <select value={form.motioner_id} onChange={e => setForm(f => ({ ...f, motioner_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  <option value="">—</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
              <div><Label className="text-xs">Seconder</Label>
                <select value={form.seconder_id} onChange={e => setForm(f => ({ ...f, seconder_id: e.target.value }))} className="w-full border border-[#DED8EB] rounded px-2 py-1.5 text-sm bg-white">
                  <option value="">—</option>{members.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Votes</Label>
              <div className="max-h-64 overflow-y-auto border border-[#DED8EB] rounded p-2 mt-1 space-y-1.5">
                {members.map(m => (
                  <div key={m.id} className="flex items-center justify-between text-xs" data-testid={`vote-row-${m.id}`}>
                    <span className="text-[#2D1B4E]">{m.name}</span>
                    <div className="flex gap-1">
                      {VOTE.map(v => (
                        <button key={v.key} onClick={() => setVote(m.id, v.key)}
                          className={`px-2 py-0.5 rounded text-[10px] font-bold border ${form.votes[m.id] === v.key ? 'text-white' : ''}`}
                          style={{
                            background: form.votes[m.id] === v.key ? v.color : 'transparent',
                            color: form.votes[m.id] === v.key ? 'white' : v.color,
                            borderColor: v.color,
                          }}
                          data-testid={`vote-btn-${m.id}-${v.key}`}>
                          {v.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div><Label className="text-xs">Notes</Label>
              <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={busy} className="bg-[#6B3FA0]" data-testid="save-resolution-btn">{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
